import { useDeviceType } from '@cian/ui-kit';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { selectTestOnboarding } from '../../selectors/onboarding/selectTestOnboarding';
import { fetchGetDesktopOnboardingsSafe } from '../../services/safeApi/fetchGetDesktopOnboardingsSafe';
import { fetchGetMobwebOnboardingsSafe } from '../../services/safeApi/fetchGetMobwebOnboardingsSafe';
import { TOnboardingData } from '../../types/onboarding';
import { useApplicationContext } from '../../utils/applicationContext';
import { useTypeSafeApiResult } from '../../utils/typesafe-result';

type TOnboardingRequestType = 'desktop' | 'phone';
type TOnboardingRequestResult = TOnboardingData | null | undefined;
type TOnboardingDataListener = (data: TOnboardingData | null) => void;

interface IOnboardingRequestEntry {
  request: Promise<TOnboardingRequestResult> | null;
  hasData: boolean;
  data: TOnboardingData | null;
  listeners: Set<TOnboardingDataListener>;
}

type TOnboardingRequests = Partial<Record<TOnboardingRequestType, IOnboardingRequestEntry>>;

interface IOnboardingResponse {
  onboardingData?: Omit<TOnboardingData, 'id'>;
  onboardingId?: number;
}

const onboardingRequestsByContext = new WeakMap<object, TOnboardingRequests>();

export function useOnboardingData(enabled = true): TOnboardingData | null {
  const ctx = useApplicationContext();
  const [onboardingData, setOnboardingData] = useState<TOnboardingData | null>(null);
  const deviceType = useDeviceType();
  const testOnboarding = useSelector(selectTestOnboarding);
  const { safeFetch: fetchDesktopData } = useTypeSafeApiResult(fetchGetDesktopOnboardingsSafe);
  const { safeFetch: fetchMobwebData } = useTypeSafeApiResult(fetchGetMobwebOnboardingsSafe);

  useEffect(() => {
    if (!enabled) {
      return;
    }
    if (testOnboarding !== null) {
      setOnboardingData(testOnboarding);

      return;
    }

    const requestType = deviceType === 'phone' ? 'phone' : 'desktop';
    const requestEntry = getOnboardingRequestEntry(ctx, requestType);
    const handleData: TOnboardingDataListener = data => setOnboardingData(data);
    requestEntry.listeners.add(handleData);

    if (requestEntry.hasData) {
      handleData(requestEntry.data);
    } else {
      ensureOnboardingRequest(requestEntry, async () => {
        let loadedData: TOnboardingRequestResult;

        if (requestType === 'phone') {
          const result = await fetchMobwebData(ctx);
          result.map(response => {
            loadedData = normalizeOnboardingData(response);
          });
        } else {
          const result = await fetchDesktopData(ctx);
          result.map(response => {
            loadedData = normalizeOnboardingData(response);
          });
        }

        return loadedData;
      });
    }

    return () => {
      requestEntry.listeners.delete(handleData);
    };
    // специально исключаем из зависимостей deviceType, чтобы не делать запрос данных
    // при изменении ширины экрана
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctx, enabled, fetchDesktopData, fetchMobwebData, testOnboarding]);

  return enabled ? onboardingData : null;
}

function getOnboardingRequestEntry(context: object, requestType: TOnboardingRequestType): IOnboardingRequestEntry {
  let requests = onboardingRequestsByContext.get(context);
  if (!requests) {
    requests = {};
    onboardingRequestsByContext.set(context, requests);
  }

  let requestEntry = requests[requestType];
  if (!requestEntry) {
    requestEntry = {
      request: null,
      hasData: false,
      data: null,
      listeners: new Set(),
    };
    requests[requestType] = requestEntry;
  }

  return requestEntry;
}

function ensureOnboardingRequest(
  requestEntry: IOnboardingRequestEntry,
  createRequest: () => Promise<TOnboardingRequestResult>,
): void {
  if (requestEntry.hasData || requestEntry.request) {
    return;
  }

  const request = createRequest().then(
    data => {
      if (requestEntry.request !== request) {
        return data;
      }
      requestEntry.request = null;
      if (data !== undefined) {
        requestEntry.data = data;
        requestEntry.hasData = true;
        [...requestEntry.listeners].forEach(listener => listener(data));
      }

      return data;
    },
    () => {
      if (requestEntry.request === request) {
        requestEntry.request = null;
      }

      return undefined;
    },
  );
  requestEntry.request = request;
}

function normalizeOnboardingData(response: IOnboardingResponse): TOnboardingData | null {
  if (!response.onboardingData || !response.onboardingId) {
    return null;
  }

  return {
    ...response.onboardingData,
    id: response.onboardingId,
  };
}
